import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { formatApiErrorDetail } from "../lib/api";
import { fmtMoney } from "../lib/format";
import {
  Check, ChevronLeft, ChevronRight, Upload, X, Plus, User as UserIcon,
  Users as UsersIcon, FileSignature, Cable, ClipboardCheck, AlertCircle,
  CheckCircle2, Sparkles, ArrowRight, Camera, Save, AlertTriangle,
} from "lucide-react";

// Used to keep one in-flight draft id per session
const DRAFT_KEY = "pm_active_draft";
function genDraftId() {
  return "draft-" + Math.random().toString(36).slice(2, 10);
}

const STEPS = [
  { id: 1, label: "Personal", icon: UserIcon, sub: "Player identity" },
  { id: 2, label: "Contacts & family", icon: UsersIcon, sub: "Who to reach" },
  { id: 3, label: "Contract & docs", icon: FileSignature, sub: "Financials" },
  { id: 4, label: "Integrations", icon: Cable, sub: "Data sources" },
  { id: 5, label: "Review", icon: ClipboardCheck, sub: "Confirm & save" },
];

const POSITIONS = [
  "Goalkeeper", "Right Back", "Left Back", "Centre Back",
  "Defensive Midfielder", "Central Midfielder", "Attacking Midfielder",
  "Right Winger", "Left Winger", "Second Striker", "Striker",
];
const FOOT_OPTIONS = ["left", "right", "both"];
const RELATION_OPTIONS = ["father", "mother", "brother", "sister", "partner", "guardian", "other"];
const LANGUAGES = ["English", "Spanish", "Portuguese", "French", "Italian", "German", "Dutch", "Other"];
const CURRENCIES = ["EUR", "GBP", "USD", "BRL", "ARS"];

const NATIONALITIES = [
  { name: "Argentina", flag: "🇦🇷" }, { name: "Brazil", flag: "🇧🇷" },
  { name: "France", flag: "🇫🇷" }, { name: "Germany", flag: "🇩🇪" },
  { name: "Italy", flag: "🇮🇹" }, { name: "Netherlands", flag: "🇳🇱" },
  { name: "Portugal", flag: "🇵🇹" }, { name: "Spain", flag: "🇪🇸" },
  { name: "England", flag: "🏴󠁧󠁢󠁥󠁮󠁧󠁿" }, { name: "Sweden", flag: "🇸🇪" },
  { name: "Norway", flag: "🇳🇴" }, { name: "Denmark", flag: "🇩🇰" },
  { name: "Belgium", flag: "🇧🇪" }, { name: "Croatia", flag: "🇭🇷" },
  { name: "Czechia", flag: "🇨🇿" }, { name: "Poland", flag: "🇵🇱" },
  { name: "Senegal", flag: "🇸🇳" }, { name: "Nigeria", flag: "🇳🇬" },
  { name: "Jamaica", flag: "🇯🇲" }, { name: "Other", flag: "🏳️" },
];

const initial = {
  // 0 — Tier (player tier system)
  tier: null,                       // 'client' | 'network' | 'mandated' — null = picker not yet answered
  can_appear_in_portfolio: true,
  relationship_notes: "",
  network_source: "direct",
  // 1
  full_name: "", nationality: "", nationality_flag: "🏳️",
  date_of_birth: "", position: "", preferred_foot: "",
  height_cm: "", weight_kg: "",
  current_club: "", current_club_badge: "",
  photo_url: "", bio: "",
  // 2
  mobile: "", email: "", whatsapp_number: "", preferred_language: "English",
  family_contacts: [{ name: "", relation: "father", phone: "", email: "" }],
  lawyer: { enabled: false, name: "", firm: "", email: "", phone: "", jurisdiction: "", specialisation: "contract_law", access_level: "read" },
  // 3
  contract_start: "", contract_end: "",
  weekly_wage: "", currency: "EUR",
  signing_fee: "", agent_commission_rate: "7.5", image_rights_pct: "",
  release_clause: "", bonus_notes: "",
  market_value: "",
  contract_status: "active",
  contract_doc_name: "", representation_doc_name: "", additional_doc_names: [],
  // 4
  integrations: {
    wyscout_id: "", scisports_id: "", transfermarkt_id: "",
    gmail_routing: true, whatsapp_routing: true, calendar_routing: true,
  },
};

export default function AddClient() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [draftId, setDraftId] = useState(() => localStorage.getItem(DRAFT_KEY) || genDraftId());
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [dupCheck, setDupCheck] = useState({ checked: false, matches: [], dismissed: false });
  const [resumePrompt, setResumePrompt] = useState(null);

  // Persist draft id
  useEffect(() => { localStorage.setItem(DRAFT_KEY, draftId); }, [draftId]);

  // Load existing draft on mount
  useEffect(() => {
    api.get("/client-drafts").then(({ data }) => {
      const drafts = (data || []).filter((d) => d.data?.full_name); // only meaningful drafts
      if (drafts.length === 0) return;
      const latest = drafts[0];
      if (latest.id === draftId) {
        // Silent resume of current session draft
        setForm({ ...initial, ...latest.data });
        setStep(latest.step || 1);
      } else {
        setResumePrompt(latest);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave (debounced)
  useEffect(() => {
    if (!form.full_name && !form.date_of_birth) return;
    const t = setTimeout(async () => {
      try {
        await api.post("/client-drafts", { id: draftId, owner: "", data: form, step });
        setDraftSavedAt(new Date());
      } catch {}
    }, 1200);
    return () => clearTimeout(t);
  }, [form, step, draftId]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateLawyer = (patch) => setForm((f) => ({ ...f, lawyer: { ...f.lawyer, ...patch } }));
  const updateIntegrations = (patch) => setForm((f) => ({ ...f, integrations: { ...f.integrations, ...patch } }));

  // Validation per step (lightweight)
  const stepErrors = useMemo(() => {
    const e = {};
    if (step === 1) {
      if (!form.full_name.trim()) e.full_name = "Required";
      if (!form.date_of_birth) e.date_of_birth = "Required";
      if (!form.nationality) e.nationality = "Pick a nationality";
      if (!form.position) e.position = "Pick a position";
      if (!form.current_club.trim()) e.current_club = "Required";
    }
    if (step === 3 && form.tier === "client") {
      if (!form.contract_start) e.contract_start = "Required";
      if (!form.contract_end) e.contract_end = "Required";
      if (!form.weekly_wage) e.weekly_wage = "Required";
    }
    return e;
  }, [step, form]);

  const canNext = Object.keys(stepErrors).length === 0;
  const isClientTier = form.tier === "client";

  const next = () => {
    if (!canNext) return;
    // Non-client tiers skip Step 3 (Contract & docs) — irrelevant for Network/Mandated
    const targetStep = !isClientTier && step === 2 ? 4 : step + 1;
    // After step 1, run duplicate check
    if (step === 1 && !dupCheck.dismissed && form.full_name && form.date_of_birth) {
      api.post("/clients/check-duplicate", {
        full_name: form.full_name, date_of_birth: form.date_of_birth,
        current_club: form.current_club,
        transfermarkt_id: form.integrations?.transfermarkt_id,
      }).then(({ data }) => {
        if ((data.matches || []).length > 0) {
          setDupCheck({ checked: true, matches: data.matches, dismissed: false });
        } else {
          setDupCheck({ checked: true, matches: [], dismissed: true });
          if (step < STEPS.length) setStep(targetStep);
        }
      }).catch(() => { if (step < STEPS.length) setStep(targetStep); });
      return;
    }
    if (step < STEPS.length) setStep(targetStep);
  };
  const back = () => {
    // Non-client tiers skip Step 3 on the way back too
    const targetStep = !isClientTier && step === 4 ? 2 : step - 1;
    if (step > 1) setStep(targetStep);
  };

  const proceedAnyway = () => {
    setDupCheck((d) => ({ ...d, dismissed: true }));
    if (step < STEPS.length) setStep(step + 1);
  };

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        nationality: form.nationality,
        nationality_flag: form.nationality_flag || "🏳️",
        date_of_birth: form.date_of_birth,
        position: form.position,
        preferred_foot: form.preferred_foot || null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        current_club: form.current_club.trim(),
        current_club_badge: form.current_club_badge || null,
        photo_url: form.photo_url || null,
        bio: form.bio || null,
        mobile: form.mobile || null,
        email: form.email || null,
        whatsapp_number: form.whatsapp_number || null,
        preferred_language: form.preferred_language || null,
        family_contacts: form.family_contacts.filter((c) => c.name?.trim()),
        lawyer: form.lawyer.enabled
          ? {
              name: form.lawyer.name || null, firm: form.lawyer.firm || null,
              email: form.lawyer.email || null, phone: form.lawyer.phone || null,
              jurisdiction: form.lawyer.jurisdiction || null,
              specialisation: form.lawyer.specialisation || null,
              access_level: form.lawyer.access_level || "read",
            }
          : null,
        contract_start: form.contract_start,
        contract_end: form.contract_end,
        contract_status: form.contract_status || "active",
        weekly_wage: Number(form.weekly_wage) || 0,
        currency: form.currency,
        market_value: Number(form.market_value) || 0,
        signing_fee: Number(form.signing_fee) || 0,
        agent_commission_rate: Number(form.agent_commission_rate) || 0,
        image_rights_pct: Number(form.image_rights_pct) || 0,
        release_clause: form.release_clause ? Number(form.release_clause) : null,
        bonus_notes: form.bonus_notes || null,
        contract_doc_name: form.contract_doc_name || null,
        representation_doc_name: form.representation_doc_name || null,
        additional_doc_names: form.additional_doc_names || [],
        integrations: {
          wyscout_id: form.integrations.wyscout_id || null,
          scisports_id: form.integrations.scisports_id || null,
          transfermarkt_id: form.integrations.transfermarkt_id || null,
          gmail_routing: !!form.integrations.gmail_routing,
          whatsapp_routing: !!form.integrations.whatsapp_routing,
          calendar_routing: !!form.integrations.calendar_routing,
        },
        tier: form.tier || "client",
        can_appear_in_portfolio: form.can_appear_in_portfolio !== false,
        relationship_notes: form.relationship_notes || null,
        network_source: form.network_source || null,
      };
      const { data } = await api.post("/clients/wizard", payload);
      // delete draft after successful save
      try { await api.delete(`/client-drafts/${draftId}`); } catch {}
      localStorage.removeItem(DRAFT_KEY);
      // attach duplicate-check result so the confirmation screen can surface it
      setConfirmation({ ...data, dup_check: dupCheck });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (confirmation) {
    return <Confirmation data={confirmation} onView={() => navigate(`/clients/${confirmation.client.id}`)} />;
  }

  // ── Pre-step: tier picker (Player Tier System Phase 1) ──
  if (!form.tier) {
    return <TierPicker onPick={(tier) => update({ tier })} />;
  }

  return (
    <Layout
      title="Add new client"
      subtitle={`Step ${step} of ${STEPS.length} · ${STEPS[step - 1].sub}`}
      right={
        <div className="flex items-center gap-2">
          {draftSavedAt && (
            <span className="hidden md:flex items-center gap-1 text-[11px] font-mono-tab text-slate-500 px-2"
                  data-testid="autosave-indicator">
              <Save size={12} className="text-emerald-500" /> Saved {draftSavedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <Link to="/clients" className="pm-btn-secondary px-4 h-10 rounded-xl text-sm font-bold flex items-center" data-testid="cancel-wizard">
            Cancel
          </Link>
        </div>
      }
    >
      {resumePrompt && (
        <div className="mb-4 pm-card p-4 flex items-center gap-3 border-l-4 border-emerald-500" data-testid="resume-prompt">
          <Save size={16} className="text-emerald-500" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-slate-900 truncate">
              Continue draft — {resumePrompt.data?.full_name || "Unnamed client"}
            </div>
            <div className="text-xs text-slate-500">
              Last edited {new Date(resumePrompt.updated_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
          <button onClick={() => { setForm({ ...initial, ...resumePrompt.data }); setStep(resumePrompt.step || 1); setDraftId(resumePrompt.id); setResumePrompt(null); }}
                  className="pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold" data-testid="resume-yes">
            Resume
          </button>
          <button onClick={() => { api.delete(`/client-drafts/${resumePrompt.id}`).catch(() => {}); setResumePrompt(null); }}
                  className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold" data-testid="resume-discard">
            Discard
          </button>
        </div>
      )}

      {dupCheck.checked && dupCheck.matches.length > 0 && !dupCheck.dismissed && (
        <div className="mb-4 pm-card p-5 border-l-4 border-amber-500" data-testid="duplicate-warning">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-amber-600" />
            <div className="font-display font-bold text-lg">Possible duplicate detected</div>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            The system found {dupCheck.matches.length} existing client{dupCheck.matches.length > 1 ? "s" : ""} that look similar.
            Confirm this is a different player before continuing.
          </p>
          <div className="space-y-2">
            {dupCheck.matches.map((m) => (
              <div key={m.id} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 flex items-center gap-3"
                   data-testid={`dup-match-${m.id}`}>
                <img src={m.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900">{m.full_name}</div>
                  <div className="text-xs text-slate-500">{m.position} · {m.current_club} · DOB {m.date_of_birth}</div>
                  <div className="text-[11px] text-amber-700 mt-0.5">{m.match_reasons.join(" · ")}</div>
                </div>
                <span className="font-mono-tab text-sm font-bold text-amber-700">{m.match_score}%</span>
                <Link to={`/clients/${m.id}`} target="_blank" rel="noreferrer"
                      className="text-xs font-bold text-slate-700 hover:text-slate-900" data-testid={`dup-view-${m.id}`}>
                  View →
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button onClick={() => setStep(1)} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold" data-testid="dup-edit">
              Go back & edit
            </button>
            <button onClick={proceedAnyway} className="pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold" data-testid="dup-proceed">
              This is a new client — continue
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-6">
        <StepRail step={step} setStep={(s) => s < step && setStep(s)} form={form} />

        <div className="pm-card p-6 lg:p-8 pm-fade">
          {step === 1 && <Step1 form={form} update={update} errors={stepErrors} />}
          {step === 2 && (
            <Step2
              form={form}
              update={update}
              updateLawyer={updateLawyer}
            />
          )}
          {step === 3 && <Step3 form={form} update={update} errors={stepErrors} />}
          {step === 4 && <Step4 form={form} updateIntegrations={updateIntegrations} />}
          {step === 5 && <Step5 form={form} setStep={setStep} />}

          {error && (
            <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg flex items-center gap-2"
                 data-testid="wizard-error">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div className="mt-8 flex items-center justify-between border-t border-slate-200 pt-5">
            <button
              onClick={back}
              disabled={step === 1}
              className="pm-btn-secondary h-11 px-4 rounded-xl text-sm font-bold flex items-center gap-1 disabled:opacity-40"
              data-testid="wizard-back-btn"
            >
              <ChevronLeft size={14} /> Back
            </button>
            <div className="text-xs text-slate-400 font-mono-tab hidden md:block">
              {step < STEPS.length ? `Next: ${STEPS[step].label}` : "Final step"}
            </div>
            {step < STEPS.length ? (
              <button
                onClick={next}
                disabled={!canNext}
                className="pm-btn-primary h-11 px-5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-60"
                data-testid="wizard-next-btn"
              >
                Continue <ChevronRight size={14} />
              </button>
            ) : (
              <button
                onClick={save}
                disabled={saving}
                className="pm-btn-primary h-11 px-5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-60"
                data-testid="wizard-save-btn"
              >
                {saving ? "Saving…" : (<>Save client <Check size={14} /></>)}
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ---------------- Step rail ---------------- */
function StepRail({ step, setStep, form }) {
  return (
    <aside className="pm-card p-5 h-fit sticky top-24" data-testid="wizard-rail">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Onboarding</div>
      <div className="space-y-1.5">
        {STEPS.map((s) => {
          const done = step > s.id;
          const active = step === s.id;
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              onClick={() => setStep(s.id)}
              data-testid={`rail-step-${s.id}`}
              className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                active
                  ? "bg-slate-900 text-white"
                  : done
                  ? "text-emerald-700 hover:bg-emerald-50"
                  : "text-slate-500 hover:bg-slate-50"
              }`}
            >
              <span className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                active ? "bg-emerald-400 text-emerald-950" :
                done ? "bg-emerald-100 text-emerald-700" :
                "bg-slate-100 text-slate-500"
              }`}>
                {done ? <Check size={14} /> : <Icon size={14} />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold leading-tight">{s.label}</div>
                <div className="text-[10px] uppercase tracking-widest opacity-70">{s.sub}</div>
              </div>
            </button>
          );
        })}
      </div>
      {form.full_name && (
        <div className="mt-5 pt-5 border-t border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Working draft</div>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-200 overflow-hidden">
              {form.photo_url && <img src={form.photo_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-sm leading-tight truncate">{form.full_name}</div>
              <div className="text-[11px] text-slate-500 truncate">{form.position || "—"} · {form.current_club || "—"}</div>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

/* ---------------- Step 1 — Personal ---------------- */
function Step1({ form, update, errors }) {
  const fileRef = useRef(null);
  const onPhotoFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) {
      alert("Please use an image under 1.5 MB, or paste a hosted URL.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => update({ photo_url: reader.result });
    reader.readAsDataURL(f);
  };

  const setNationality = (name) => {
    const found = NATIONALITIES.find((n) => n.name === name);
    update({ nationality: name, nationality_flag: found?.flag || "🏳️" });
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-bold tracking-tight">Personal details</h2>
      <p className="text-slate-500 text-sm mt-1">The basics for the briefing card.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Photo</div>
          <div
            onClick={() => fileRef.current?.click()}
            className="aspect-[3/4] rounded-xl overflow-hidden border-2 border-dashed border-slate-300 hover:border-emerald-500 transition-all cursor-pointer bg-slate-50 flex items-center justify-center relative group"
            data-testid="photo-drop-zone"
          >
            {form.photo_url ? (
              <>
                <img src={form.photo_url} alt="player" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold">
                  <Camera size={16} className="mr-1" /> Replace
                </div>
              </>
            ) : (
              <div className="text-center text-slate-500 px-4">
                <Upload size={20} className="mx-auto mb-2 text-slate-400" />
                <div className="text-xs font-bold">Click to upload</div>
                <div className="text-[10px] mt-1">PNG / JPG · ≤1.5 MB</div>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoFile} data-testid="photo-file-input" />
          </div>
          <input
            value={typeof form.photo_url === "string" && form.photo_url.startsWith("data:") ? "" : form.photo_url}
            onChange={(e) => update({ photo_url: e.target.value })}
            placeholder="or paste image URL"
            className="pm-input mt-2 text-xs"
            data-testid="photo-url-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full name" required error={errors.full_name}>
            <input className="pm-input" value={form.full_name} onChange={(e) => update({ full_name: e.target.value })}
                   placeholder="Lucas Reyes" data-testid="field-full_name" />
          </Field>
          <Field label="Date of birth" required error={errors.date_of_birth}>
            <input type="date" className="pm-input" value={form.date_of_birth}
                   onChange={(e) => update({ date_of_birth: e.target.value })} data-testid="field-dob" />
          </Field>

          <Field label="Nationality" required error={errors.nationality}>
            <div className="flex gap-2">
              <select className="pm-input flex-1" value={form.nationality}
                      onChange={(e) => setNationality(e.target.value)} data-testid="field-nationality">
                <option value="">Select…</option>
                {NATIONALITIES.map((n) => <option key={n.name} value={n.name}>{n.name}</option>)}
              </select>
              <div className="w-12 h-11 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-xl">
                {form.nationality_flag}
              </div>
            </div>
          </Field>
          <Field label="Position" required error={errors.position}>
            <select className="pm-input" value={form.position} onChange={(e) => update({ position: e.target.value })}
                    data-testid="field-position">
              <option value="">Select…</option>
              {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>

          <Field label="Preferred foot">
            <div className="grid grid-cols-3 gap-2">
              {FOOT_OPTIONS.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => update({ preferred_foot: f })}
                  data-testid={`foot-${f}`}
                  className={`h-11 rounded-lg text-sm font-bold border-2 capitalize transition-all ${
                    form.preferred_foot === f
                      ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Height (cm)">
              <input type="number" min="100" max="230" className="pm-input" value={form.height_cm}
                     onChange={(e) => update({ height_cm: e.target.value })} placeholder="182" data-testid="field-height" />
            </Field>
            <Field label="Weight (kg)">
              <input type="number" min="40" max="150" className="pm-input" value={form.weight_kg}
                     onChange={(e) => update({ weight_kg: e.target.value })} placeholder="76" data-testid="field-weight" />
            </Field>
          </div>

          <Field label="Current club" required error={errors.current_club}>
            <input className="pm-input" value={form.current_club} onChange={(e) => update({ current_club: e.target.value })}
                   placeholder="Ajax FC" data-testid="field-current_club" />
          </Field>
          <Field label="Club badge URL (optional)">
            <input className="pm-input" value={form.current_club_badge}
                   onChange={(e) => update({ current_club_badge: e.target.value })}
                   placeholder="https://…" data-testid="field-club_badge" />
          </Field>

          <div className="md:col-span-2">
            <Field label="Short bio (optional)">
              <textarea className="pm-input" rows={3} value={form.bio}
                        onChange={(e) => update({ bio: e.target.value })}
                        placeholder="Two-line scout-eye view to brief the team before a call…" data-testid="field-bio" />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Step 2 — Contacts & family ---------------- */
function Step2({ form, update, updateLawyer }) {
  const addFamily = () => update({ family_contacts: [...form.family_contacts, { name: "", relation: "other", phone: "", email: "" }] });
  const removeFamily = (i) => update({ family_contacts: form.family_contacts.filter((_, idx) => idx !== i) });
  const updateFamily = (i, patch) => update({
    family_contacts: form.family_contacts.map((c, idx) => idx === i ? { ...c, ...patch } : c),
  });

  return (
    <div>
      <h2 className="font-display text-2xl font-bold tracking-tight">Contacts & family</h2>
      <p className="text-slate-500 text-sm mt-1">Who do you reach when something happens?</p>

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Player</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Mobile">
            <input className="pm-input" value={form.mobile} onChange={(e) => update({ mobile: e.target.value })}
                   placeholder="+34 6 0000 0000" data-testid="field-mobile" />
          </Field>
          <Field label="Email">
            <input className="pm-input" value={form.email} onChange={(e) => update({ email: e.target.value })}
                   placeholder="lucas@reyes.fc" data-testid="field-email" />
          </Field>
          <Field label="WhatsApp">
            <input className="pm-input" value={form.whatsapp_number}
                   onChange={(e) => update({ whatsapp_number: e.target.value })}
                   placeholder="+34 6 0000 0000" data-testid="field-whatsapp" />
          </Field>
          <Field label="Preferred language">
            <select className="pm-input" value={form.preferred_language}
                    onChange={(e) => update({ preferred_language: e.target.value })}
                    data-testid="field-language">
              {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Family contacts</div>
          <button onClick={addFamily} className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1"
                  data-testid="add-family-contact">
            <Plus size={12} /> Add contact
          </button>
        </div>
        <div className="space-y-3">
          {form.family_contacts.map((c, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-4" data-testid={`family-row-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                <input className="pm-input md:col-span-3" placeholder="Name" value={c.name}
                       onChange={(e) => updateFamily(i, { name: e.target.value })}
                       data-testid={`family-name-${i}`} />
                <select className="pm-input md:col-span-2" value={c.relation}
                        onChange={(e) => updateFamily(i, { relation: e.target.value })}
                        data-testid={`family-relation-${i}`}>
                  {RELATION_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <input className="pm-input md:col-span-3" placeholder="Phone" value={c.phone}
                       onChange={(e) => updateFamily(i, { phone: e.target.value })}
                       data-testid={`family-phone-${i}`} />
                <input className="pm-input md:col-span-3" placeholder="Email" value={c.email}
                       onChange={(e) => updateFamily(i, { email: e.target.value })}
                       data-testid={`family-email-${i}`} />
                <button onClick={() => removeFamily(i)} className="md:col-span-1 h-10 text-slate-400 hover:text-red-600 transition-colors flex items-center justify-center"
                        data-testid={`family-remove-${i}`}>
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-slate-200 p-5 bg-slate-50/40" data-testid="lawyer-section">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-bold text-slate-900">Lawyer / legal representative</div>
            <div className="text-xs text-slate-500">Optional — grant access to client legal documents.</div>
          </div>
          <Toggle checked={form.lawyer.enabled} onChange={(v) => updateLawyer({ enabled: v })} testid="lawyer-toggle" />
        </div>
        {form.lawyer.enabled && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Lawyer name"><input className="pm-input" value={form.lawyer.name}
                   onChange={(e) => updateLawyer({ name: e.target.value })} data-testid="lawyer-name" /></Field>
            <Field label="Firm"><input className="pm-input" value={form.lawyer.firm}
                   onChange={(e) => updateLawyer({ firm: e.target.value })} data-testid="lawyer-firm" /></Field>
            <Field label="Email"><input className="pm-input" value={form.lawyer.email}
                   onChange={(e) => updateLawyer({ email: e.target.value })} data-testid="lawyer-email" /></Field>
            <Field label="Phone"><input className="pm-input" value={form.lawyer.phone}
                   onChange={(e) => updateLawyer({ phone: e.target.value })} data-testid="lawyer-phone" /></Field>
            <Field label="Jurisdiction"><input className="pm-input" placeholder="Netherlands / EU"
                   value={form.lawyer.jurisdiction}
                   onChange={(e) => updateLawyer({ jurisdiction: e.target.value })} data-testid="lawyer-jurisdiction" /></Field>
            <Field label="Specialisation">
              <select className="pm-input" value={form.lawyer.specialisation}
                      onChange={(e) => updateLawyer({ specialisation: e.target.value })}
                      data-testid="lawyer-specialisation">
                <option value="contract_law">Contract law</option>
                <option value="immigration">Immigration</option>
                <option value="image_rights">Image rights</option>
                <option value="tax">Tax</option>
                <option value="labour_disputes">Labour disputes</option>
                <option value="general">General</option>
              </select>
            </Field>
            <Field label="Access level">
              <div className="grid grid-cols-3 gap-2">
                {["read", "comment", "edit"].map((a) => (
                  <button key={a} type="button" onClick={() => updateLawyer({ access_level: a })}
                          data-testid={`lawyer-access-${a}`}
                          className={`h-11 rounded-lg text-xs font-bold capitalize border-2 transition-all ${
                            form.lawyer.access_level === a
                              ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                          }`}>
                    {a}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Step 3 — Contract & docs ---------------- */
function Step3({ form, update, errors }) {
  const addDoc = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    update({ additional_doc_names: [...form.additional_doc_names, f.name] });
    e.target.value = "";
  };
  const removeDoc = (i) => update({ additional_doc_names: form.additional_doc_names.filter((_, idx) => idx !== i) });

  return (
    <div>
      <h2 className="font-display text-2xl font-bold tracking-tight">Contract & documents</h2>
      <p className="text-slate-500 text-sm mt-1">The financial structure plus the paperwork that backs it up.</p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Contract status">
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "active", label: "Active" },
              { v: "in_negotiation", label: "In negotiation" },
              { v: "pre_contract", label: "Pre-contract" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => update({ contract_status: opt.v })}
                data-testid={`contract-status-${opt.v}`}
                className={`h-10 rounded-lg text-xs font-bold border-2 transition-all ${
                  form.contract_status === opt.v
                    ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Field>
        <div />
        <Field label="Contract start" required error={errors.contract_start}>
          <input type="date" className="pm-input" value={form.contract_start}
                 onChange={(e) => update({ contract_start: e.target.value })} data-testid="field-contract_start" />
        </Field>
        <Field label="Contract end" required error={errors.contract_end}>
          <input type="date" className="pm-input" value={form.contract_end}
                 onChange={(e) => update({ contract_end: e.target.value })} data-testid="field-contract_end" />
        </Field>

        <Field label="Currency">
          <select className="pm-input" value={form.currency} onChange={(e) => update({ currency: e.target.value })}
                  data-testid="field-currency">
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Weekly wage" required error={errors.weekly_wage}>
          <input type="number" className="pm-input font-mono-tab" value={form.weekly_wage}
                 onChange={(e) => update({ weekly_wage: e.target.value })} placeholder="95000"
                 data-testid="field-weekly_wage" />
          {form.weekly_wage && <div className="text-[11px] text-emerald-700 font-mono-tab mt-1" data-testid="preview-weekly_wage">= {fmtMoney(Number(form.weekly_wage))}</div>}
        </Field>

        <Field label="Signing fee (lump)">
          <input type="number" className="pm-input font-mono-tab" value={form.signing_fee}
                 onChange={(e) => update({ signing_fee: e.target.value })} placeholder="500000"
                 data-testid="field-signing_fee" />
          {form.signing_fee && <div className="text-[11px] text-emerald-700 font-mono-tab mt-1">= {fmtMoney(Number(form.signing_fee))}</div>}
        </Field>
        <Field label="Market value (€M)">
          <input type="number" step="0.1" className="pm-input font-mono-tab" value={form.market_value}
                 onChange={(e) => update({ market_value: e.target.value })} placeholder="42"
                 data-testid="field-market_value" />
          {form.market_value && <div className="text-[11px] text-emerald-700 font-mono-tab mt-1">= {fmtMoney(Number(form.market_value) * 1_000_000)}</div>}
        </Field>

        <Field label="Agent commission (%)">
          <input type="number" step="0.1" className="pm-input font-mono-tab" value={form.agent_commission_rate}
                 onChange={(e) => update({ agent_commission_rate: e.target.value })} placeholder="7.5"
                 data-testid="field-commission" />
        </Field>
        <Field label="Image rights (%)">
          <input type="number" step="0.1" className="pm-input font-mono-tab" value={form.image_rights_pct}
                 onChange={(e) => update({ image_rights_pct: e.target.value })} placeholder="15"
                 data-testid="field-image_rights" />
        </Field>

        <Field label="Release clause">
          <input type="number" className="pm-input font-mono-tab" value={form.release_clause}
                 onChange={(e) => update({ release_clause: e.target.value })} placeholder="80000000"
                 data-testid="field-release_clause" />
          {form.release_clause && <div className="text-[11px] text-emerald-700 font-mono-tab mt-1" data-testid="preview-release_clause">= {fmtMoney(Number(form.release_clause))}</div>}
        </Field>
        <div />

        <div className="md:col-span-2">
          <Field label="Bonus notes">
            <textarea className="pm-input" rows={2} value={form.bonus_notes}
                      onChange={(e) => update({ bonus_notes: e.target.value })}
                      placeholder="€8k per goal, €5k per appearance, €250k UCL qualification, €100k loyalty triggered at 50 apps…"
                      data-testid="field-bonus_notes" />
          </Field>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <DocSlot label="Signed player contract" value={form.contract_doc_name}
                 onPick={(name) => update({ contract_doc_name: name })}
                 onClear={() => update({ contract_doc_name: "" })}
                 testid="doc-contract" />
        <DocSlot label="Representation agreement" value={form.representation_doc_name}
                 onPick={(name) => update({ representation_doc_name: name })}
                 onClear={() => update({ representation_doc_name: "" })}
                 testid="doc-representation" />
        <div className="rounded-xl border-2 border-dashed border-slate-300 p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Additional documents</div>
          <label className="text-xs font-bold text-emerald-700 hover:text-emerald-800 cursor-pointer flex items-center gap-1">
            <Plus size={12} /> Add file
            <input type="file" className="hidden" onChange={addDoc} data-testid="doc-additional-input" />
          </label>
          <div className="mt-3 space-y-1">
            {form.additional_doc_names.map((n, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"
                   data-testid={`doc-additional-${i}`}>
                <span className="truncate">{n}</span>
                <button onClick={() => removeDoc(i)} className="text-slate-400 hover:text-red-600"><X size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DocSlot({ label, value, onPick, onClear, testid }) {
  const ref = useRef(null);
  return (
    <div className="rounded-xl border-2 border-dashed border-slate-300 p-4 hover:border-emerald-500 transition-colors" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">{label}</div>
      {value ? (
        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5 text-xs">
          <span className="truncate font-bold text-emerald-800">{value}</span>
          <button onClick={onClear} className="text-emerald-700 hover:text-red-600" data-testid={`${testid}-clear`}><X size={12} /></button>
        </div>
      ) : (
        <button
          onClick={() => ref.current?.click()}
          className="w-full text-left text-xs font-bold text-slate-500 flex items-center gap-2 hover:text-emerald-700"
          data-testid={`${testid}-btn`}
        >
          <Upload size={14} /> Click to upload PDF / DOCX
        </button>
      )}
      <input ref={ref} type="file" className="hidden" onChange={(e) => {
        const f = e.target.files?.[0]; if (f) onPick(f.name);
        e.target.value = "";
      }} />
    </div>
  );
}

/* ---------------- Step 4 — Integrations ---------------- */
function Step4({ form, updateIntegrations }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold tracking-tight">Integrations</h2>
      <p className="text-slate-500 text-sm mt-1">Connect external data sources so the client's homepage updates itself.</p>

      <div className="mt-6 space-y-4">
        <IntegRow
          name="Wyscout"
          desc="Per-season stats, key passes, progressive carries, duels won."
          idLabel="Wyscout player ID"
          idValue={form.integrations.wyscout_id}
          onId={(v) => updateIntegrations({ wyscout_id: v })}
          testid="integ-wyscout"
        />
        <IntegRow
          name="SciSports"
          desc="Player ratings, position percentiles, radar profile."
          idLabel="SciSports player ID"
          idValue={form.integrations.scisports_id}
          onId={(v) => updateIntegrations({ scisports_id: v })}
          testid="integ-scisports"
        />
        <IntegRow
          name="Transfermarkt"
          desc="Live market value, transfer history, profile activity."
          idLabel="Transfermarkt profile slug"
          idValue={form.integrations.transfermarkt_id}
          onId={(v) => updateIntegrations({ transfermarkt_id: v })}
          testid="integ-transfermarkt"
        />
      </div>

      <div className="mt-8 rounded-xl border border-slate-200 p-5 bg-slate-50/40">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Routing for this client</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <RoutingToggle label="Gmail / Outlook" desc="Route emails into unified inbox"
                         checked={form.integrations.gmail_routing}
                         onChange={(v) => updateIntegrations({ gmail_routing: v })}
                         testid="routing-gmail" />
          <RoutingToggle label="WhatsApp & SMS" desc="Route to shared agency number"
                         checked={form.integrations.whatsapp_routing}
                         onChange={(v) => updateIntegrations({ whatsapp_routing: v })}
                         testid="routing-whatsapp" />
          <RoutingToggle label="Google Calendar" desc="Two-way sync (mocked)"
                         checked={form.integrations.calendar_routing}
                         onChange={(v) => updateIntegrations({ calendar_routing: v })}
                         testid="routing-calendar" />
        </div>
      </div>
    </div>
  );
}

function IntegRow({ name, desc, idLabel, idValue, onId, testid }) {
  const connected = !!idValue;
  return (
    <div className={`rounded-xl border p-4 transition-all ${connected ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
         data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-base">{name}</span>
            {connected ? (
              <span className="pm-pill pm-pill-green">Connected</span>
            ) : (
              <span className="pm-pill pm-pill-slate">Not linked</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
        </div>
      </div>
      <input
        value={idValue}
        onChange={(e) => onId(e.target.value)}
        placeholder={idLabel}
        className="pm-input mt-3 font-mono-tab text-sm"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}

function RoutingToggle({ label, desc, checked, onChange, testid }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-3 flex items-start gap-3">
      <Toggle checked={checked} onChange={onChange} testid={testid} />
      <div>
        <div className="font-bold text-sm">{label}</div>
        <div className="text-[11px] text-slate-500">{desc}</div>
      </div>
    </div>
  );
}

/* ---------------- Step 5 — Review ---------------- */
function Step5({ form, setStep }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-bold tracking-tight">Review & save</h2>
      <p className="text-slate-500 text-sm mt-1">One last look before this client goes live.</p>

      <div className="mt-6 space-y-4">
        <ReviewBlock title="Personal" onEdit={() => setStep(1)} testid="review-personal">
          <ReviewRow label="Full name" value={form.full_name} />
          <ReviewRow label="DOB" value={form.date_of_birth} />
          <ReviewRow label="Nationality" value={`${form.nationality_flag} ${form.nationality}`} />
          <ReviewRow label="Position" value={form.position} />
          <ReviewRow label="Foot" value={form.preferred_foot} />
          <ReviewRow label="Height / weight" value={form.height_cm && form.weight_kg ? `${form.height_cm} cm / ${form.weight_kg} kg` : "—"} />
          <ReviewRow label="Current club" value={form.current_club} />
        </ReviewBlock>

        <ReviewBlock title="Contacts & family" onEdit={() => setStep(2)} testid="review-contacts">
          <ReviewRow label="Mobile" value={form.mobile} />
          <ReviewRow label="Email" value={form.email} />
          <ReviewRow label="WhatsApp" value={form.whatsapp_number} />
          <ReviewRow label="Language" value={form.preferred_language} />
          <ReviewRow label="Family" value={form.family_contacts.filter((c) => c.name).map((c) => `${c.name} (${c.relation})`).join(", ") || "—"} />
          <ReviewRow label="Lawyer" value={form.lawyer.enabled ? `${form.lawyer.name || "—"}${form.lawyer.firm ? ` · ${form.lawyer.firm}` : ""} · ${form.lawyer.access_level}` : "—"} />
        </ReviewBlock>

        <ReviewBlock title="Contract" onEdit={() => setStep(3)} testid="review-contract">
          <ReviewRow label="Period" value={`${form.contract_start} → ${form.contract_end}`} />
          <ReviewRow label="Weekly wage" value={form.weekly_wage ? fmtMoney(Number(form.weekly_wage), form.currency) : "—"} />
          <ReviewRow label="Signing fee" value={form.signing_fee ? fmtMoney(Number(form.signing_fee), form.currency) : "—"} />
          <ReviewRow label="Agent commission" value={form.agent_commission_rate ? `${form.agent_commission_rate}%` : "—"} />
          <ReviewRow label="Image rights" value={form.image_rights_pct ? `${form.image_rights_pct}%` : "—"} />
          <ReviewRow label="Release clause" value={form.release_clause ? fmtMoney(Number(form.release_clause), form.currency) : "—"} />
          <ReviewRow label="Bonus notes" value={form.bonus_notes || "—"} />
          <ReviewRow label="Documents" value={[
            form.contract_doc_name, form.representation_doc_name, ...form.additional_doc_names,
          ].filter(Boolean).join(", ") || "—"} />
        </ReviewBlock>

        <ReviewBlock title="Integrations" onEdit={() => setStep(4)} testid="review-integrations">
          <ReviewRow label="Wyscout ID" value={form.integrations.wyscout_id || "Not linked"} />
          <ReviewRow label="SciSports ID" value={form.integrations.scisports_id || "Not linked"} />
          <ReviewRow label="Transfermarkt" value={form.integrations.transfermarkt_id || "Not linked"} />
          <ReviewRow label="Email routing" value={form.integrations.gmail_routing ? "On" : "Off"} />
          <ReviewRow label="WhatsApp routing" value={form.integrations.whatsapp_routing ? "On" : "Off"} />
          <ReviewRow label="Calendar sync" value={form.integrations.calendar_routing ? "On" : "Off"} />
        </ReviewBlock>
      </div>
    </div>
  );
}

function ReviewBlock({ title, onEdit, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold tracking-tight">{title}</div>
        <button onClick={onEdit} className="text-xs font-bold text-emerald-700 hover:text-emerald-800" data-testid={`${testid}-edit`}>
          Edit
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-slate-100 last:border-0">
      <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 shrink-0">{label}</div>
      <div className="font-mono-tab text-sm text-slate-800 text-right truncate">{value || "—"}</div>
    </div>
  );
}

/* ---------------- Confirmation ---------------- */
function Confirmation({ data, onView }) {
  return (
    <Layout title="Client created" subtitle="Live across the platform">
      <div className="max-w-3xl mx-auto pm-fade">
        <div className="pm-card p-8 text-center relative overflow-hidden">
          <div aria-hidden className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-40"
               style={{ background: "radial-gradient(circle, rgba(0,230,118,0.4), transparent 70%)" }} />
          <div className="relative">
            <div className="w-16 h-16 rounded-full mx-auto bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 size={28} className="text-emerald-700" />
            </div>
            <h2 className="font-display text-3xl font-black tracking-tight mt-4">
              {data.client.full_name} is now on the books
            </h2>
            <p className="text-slate-500 mt-2">
              Player contract and representation agreement filed. Routing is active across all channels.
            </p>

            <div className="mt-6 inline-flex items-center gap-4 px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="w-12 h-12 rounded-xl bg-slate-200 overflow-hidden">
                {data.client.photo_url && <img src={data.client.photo_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="text-left">
                <div className="font-display font-bold leading-tight">{data.client.full_name}</div>
                <div className="text-xs text-slate-500">{data.client.position} · {data.client.current_club}</div>
              </div>
            </div>

            {/* Duplicate-check result */}
            {data.dup_check && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border"
                   data-testid="confirm-dup-result"
                   style={data.dup_check.matches?.length
                     ? { borderColor: "#fcd34d", background: "#fffbeb", color: "#92400e" }
                     : { borderColor: "#a7f3d0", background: "#ecfdf5", color: "#065f46" }}>
                {data.dup_check.matches?.length
                  ? <><AlertTriangle size={12} /> Duplicate check: {data.dup_check.matches.length} similar profile{data.dup_check.matches.length > 1 ? "s" : ""} flagged — confirmed as new client</>
                  : <><CheckCircle2 size={12} /> Duplicate check: no similar profiles found</>}
              </div>
            )}

            <div className="mt-8 text-left">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-1">
                <Sparkles size={11} className="text-emerald-500" /> Sync status
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.sync_status.map((s) => (
                  <div key={s.service}
                       className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
                         s.connected ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
                       }`}
                       data-testid={`sync-${s.service.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      s.connected ? "bg-emerald-500 text-white" : "bg-slate-300 text-slate-600"
                    }`}>
                      {s.connected ? <Check size={14} /> : <X size={14} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-slate-900">{s.service}</div>
                      <div className="text-[11px] text-slate-500 truncate">{s.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/clients" className="pm-btn-secondary h-11 px-5 rounded-xl text-sm font-bold" data-testid="confirm-back-to-roster">
                Back to roster
              </Link>
              <button onClick={onView} className="pm-btn-primary h-11 px-5 rounded-xl text-sm font-bold flex items-center gap-2"
                      data-testid="confirm-open-client">
                Open client homepage <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ---------------- shared field bits ---------------- */
function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1 mb-1.5">
        {label}{required && <span className="text-emerald-600">·</span>}
      </label>
      {children}
      {error && <div className="text-[11px] text-red-600 mt-1 font-bold">{error}</div>}
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`w-10 h-6 rounded-full p-0.5 transition-colors duration-200 shrink-0 ${
        checked ? "bg-emerald-500" : "bg-slate-300"
      }`}
    >
      <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
        checked ? "translate-x-4" : "translate-x-0"
      }`} />
    </button>
  );
}


function TierPicker({ onPick }) {
  const TIERS = [
    {
      id: "client",
      title: "Client",
      blurb: "You represent this player officially with a signed agreement.",
      detail: "Full profile · Contracts · Cases · Legal · Inbox · Payroll · Travel planner · Outgoing mandates",
      label: "Represented by your agency",
    },
    {
      id: "network",
      title: "Network player",
      blurb: "You can introduce this player to clubs but they don't have a signed agreement with you.",
      detail: "Profile · Market value · Stats · Radar · Reports & portfolios",
      label: "Available for introduction",
    },
    {
      id: "mandated",
      title: "Mandated player",
      blurb: "Another agent has mandated you to place this player at specific clubs or markets.",
      detail: "Profile · Mandate document · Scope tracking · Expiry alerts · Reports",
      label: "Mandated — collaboration with partner agency",
    },
  ];
  return (
    <Layout title="Add a new player" subtitle="Pick the type to get the right workflow">
      <div className="max-w-3xl mx-auto" data-testid="tier-picker">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">Step 0 of 5 · Player type</div>
        <div className="font-display text-2xl font-black tracking-tight mb-1">What type of player are you adding?</div>
        <div className="text-sm text-slate-600 mb-5">This determines what's available in the profile, what appears in reports, and which sections are visible.</div>
        <div className="space-y-3">
          {TIERS.map((t) => (
            <button
              key={t.id}
              onClick={() => onPick(t.id)}
              data-testid={`tier-pick-${t.id}`}
              className="w-full text-left p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-emerald-400 hover:shadow-sm transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg font-bold text-slate-900">{t.title}</div>
                    <TierBadgeStatic tier={t.id} />
                  </div>
                  <div className="text-[13px] text-slate-700 mt-1 leading-snug">{t.blurb}</div>
                  <div className="text-[11px] text-slate-500 mt-1.5 italic">{t.detail}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400 mt-2">Report label · "{t.label}"</div>
                </div>
                <ArrowRight size={16} className="text-slate-300 group-hover:text-emerald-600 transition-colors mt-1.5 shrink-0" />
              </div>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-4 text-center">
          You can change a player's tier later from their profile (admin only).
        </p>
      </div>
    </Layout>
  );
}

function TierBadgeStatic({ tier }) {
  const META = {
    client:   { label: "CLIENT",   cls: "bg-emerald-500 text-white" },
    network:  { label: "NETWORK",  cls: "bg-white text-blue-700 border-2 border-blue-400" },
    mandated: { label: "MANDATE",  cls: "bg-amber-500 text-white" },
  };
  const m = META[tier] || META.client;
  return (
    <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-black tracking-wider ${m.cls}`} data-testid={`tier-badge-static-${tier}`}>
      {m.label}
    </span>
  );
}
